import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, ShieldCheck, TrendingDown, Sparkles, X, Search, Calendar, MessagesSquare, ChevronRight } from "lucide-react";
import api, { productUrl } from "../lib/api";
import ProductCardText, { ProductCardSkeleton } from "../components/ProductCardText";
import BhegaButton from "../components/BhegaButton";
import useLiveCount from "../lib/useLiveCount";
import { useAuth } from "../lib/auth";
import { useActivity, fmtMonth, timeAgo } from "../lib/activity";

const CAT_SUBTITLES = {
  automobile: "Hyundai · Mahindra · Kia · MG · Honda",
  car: "Hyundai · Mahindra · Kia · BMW · Mercedes",
  bike: "Honda · Bajaj · Royal Enfield",
  real_estate: "Sun Builders · Gala Infra",
  electronics: "Apple · Samsung",
};

const STEPS = [
  { i: <ShieldCheck size={16} />, t: "Sign Up Securely", s: "Create your free BHEGA account. Your name and phone number remain private and are never shared with other buyers." },
  { i: <Search size={16} />, t: "Choose What You Want to Buy", s: "Search and select the product you're planning to purchase." },
  { i: <span aria-hidden>✊</span>, t: "Hit the BHEGA Button", s: "Click BHEGA to express your genuine buying intent." },
  { i: <Calendar size={16} />, t: "Select Your Buying Month", s: "Choose the month you intend to buy. This connects you with buyers on the same timeline." },
  { i: <MessagesSquare size={16} />, t: "Join Your Negotiation Room", s: "A dedicated room for everyone buying the same product in the same month." },
  { i: <Sparkles size={16} />, t: "Discuss & Compare", s: "Talk features, prices, sellers, offers, financing and real buying experiences." },
  { i: <Users size={16} />, t: "Organize Together", s: "Plan an online meeting, meet at a café, or visit the showroom as a group." },
  { i: <TrendingDown size={16} />, t: "Negotiate as One", s: "Approach the seller together and use your collective buying power." },
];

function BhegaInRow({ item, index }) {
  const cityShort = (item.city || "").split(",")[0].trim();
  const unread = item.unread_count || 0;
  return (
    <Link
      to={`/${item.product_slug || item.product_id}`}
      className="group flex items-center gap-3 px-4 py-3 bg-card hover:bg-raised transition-colors"
      data-testid={`bhega-in-row-${index}`}
    >
      <span className={`w-1 self-stretch shrink-0 ${unread ? "bg-signal" : "bg-[var(--bhega-border)]"}`} aria-hidden />
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-[15px] truncate">{item.product_name}</span>
        <span className="block text-xs text-muted-foreground mt-0.5 truncate">
          {cityShort} · {fmtMonth(item.date)} · <span className="font-bold text-foreground num">✊ {item.bhega_count}</span> buyers
        </span>
      </span>
      {unread > 0 && <span className="badge-count" data-testid={`bhega-in-unread-${index}`}>{unread} new</span>}
      <span className="text-xs font-bold inline-flex items-center gap-0.5 text-muted-foreground group-hover:text-foreground" data-testid={`bhega-in-room-${index}`}>
        Room <ChevronRight size={13} />
      </span>
    </Link>
  );
}

function BhegaInModal({ items, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-[var(--bhega-scrim)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-lg border border-border max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bhega-in-modal" role="dialog" aria-label="All your rooms">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card">
          <h3 className="font-extrabold text-base">All your rooms</h3>
          <button onClick={onClose} className="icon-btn" data-testid="bhega-in-modal-close" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="divide-y divide-border">
          {items.map((it, i) => <BhegaInRow key={`${it.product_id}-${it.scope}`} item={it} index={`m-${i}`} />)}
        </div>
      </div>
    </div>
  );
}

function Stats({ stats }) {
  if (!stats) return <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[var(--bhega-border)] border border-border">{[0, 1, 2, 3].map((i) => <div key={i} className="bg-card p-4"><div className="skeleton h-8 w-16" /></div>)}</div>;
  const rows = [
    { k: "stat-products", v: stats.products, l: "Products listed" },
    { k: "stat-users", v: stats.users, l: "Buyers on BHEGA" },
    { k: "stat-active-rooms", v: stats.active_rooms ?? 0, l: "Active rooms", hl: true },
    { k: "stat-active-users", v: stats.active_users ?? stats.users, l: "Buyers in rooms" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[var(--bhega-border)] border border-border">
      {rows.map((r) => (
        <div key={r.k} className={`p-4 bg-card ${r.hl ? "shadow-[inset_0_3px_0_0_#FFBE06]" : ""}`} data-testid={r.k}>
          <div className="text-3xl font-extrabold tracking-tight num">{r.v}</div>
          <div className={`text-xs font-bold uppercase tracking-[0.1em] mt-1 text-muted-foreground`}>{r.l}</div>
        </div>
      ))}
    </div>
  );
}

function Spotlight({ product }) {
  const liveCount = useLiveCount(product);
  if (!product) return <div className="bhega-card h-full min-h-[320px] p-6"><div className="skeleton h-4 w-24" /><div className="skeleton h-10 w-3/4 mt-4" /></div>;
  return (
    <div className="bhega-card flex flex-col" data-testid="featured-product-card">
      <div className="h-1 bg-[#FFBE06]" aria-hidden />
      <Link to={productUrl(product)} className="p-6 sm:p-8 flex-1 flex flex-col">
        <span className="bhega-pill pill-dark w-fit">Room spotlight</span>
        <div className="eyebrow mt-6">{product.category_label}</div>
        <div className="text-4xl sm:text-5xl font-extrabold tracking-[-0.04em] leading-[1] mt-2">{product.product || product.name}</div>
        <div className="text-sm text-muted-foreground mt-2 font-medium">{[product.brand, product.typology || product.variant].filter(Boolean).join(" · ")}</div>
        <div className="mt-10 grid grid-cols-2 gap-4">
          <div><div className="text-4xl font-extrabold num">{product.bhega_count ?? 0}</div><div className="eyebrow mt-1">Buyers united</div></div>
          <div><div className="text-4xl font-extrabold num">{product.comment_count ?? 0}</div><div className="eyebrow mt-1">Messages</div></div>
        </div>
      </Link>
      <div className="px-6 sm:px-8 pb-6 sm:pb-8">
        <BhegaButton product={product} count={liveCount} size="lg" testId="featured-bhega-btn" />
      </div>
    </div>
  );
}

function Hero({ user, onOpenAuth, stats, featured }) {
  return (
    <section className="border-b border-border">
      <div className="container-x py-12 sm:py-20 grid lg:grid-cols-[1.45fr_1fr] gap-10 lg:gap-12 items-start">
        <div className="slide-up flex flex-col">
          <div className="bhega-pill pill-glow w-fit" data-testid="hero-pill"><span aria-hidden>✊</span> India&apos;s social group-buying platform</div>
          <h1 className="display-1 mt-6">
            Together,<br />
            We Buy Better.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[var(--bhega-text)] max-w-xl leading-relaxed">
            Join buyers planning the same purchase in the same month. Compare real quotes, share experiences, and walk into the showroom as a group.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/browse" className="btn-primary" data-testid="hero-cta-explore">Find your room <ArrowRight size={16} /></Link>
            {!user && <button onClick={onOpenAuth} className="btn-secondary" data-testid="hero-cta-signin">Sign in with WhatsApp</button>}
          </div>
          <div className="mt-auto pt-10"><Stats stats={stats} /></div>
        </div>
        <Spotlight product={featured} />
      </div>
    </section>
  );
}

function Dashboard({ user, rooms, notifications, onShowAll }) {
  const totalUnread = notifications.reduce((a, n) => a + (n.unread_count || 0), 0);
  return (
    <section className="border-b border-border bg-paper-2" data-testid="home-dashboard">
      <div className="container-x py-10 sm:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 slide-up">
          <div>
            <div className="eyebrow">Your BHEGA</div>
            <h1 className="t-page mt-1" data-testid="dashboard-greeting">
              Welcome back, {(user.name || "buyer").split(" ")[0]}.
            </h1>
            <p className="text-muted-foreground mt-1.5">
              {totalUnread > 0
                ? <><strong className="text-signal num">{totalUnread} new message{totalUnread === 1 ? "" : "s"}</strong> waiting in your rooms.</>
                : rooms.length ? "You're all caught up. Keep the momentum going." : "Raise your fist on something you plan to buy to get started."}
            </p>
          </div>
          <Link to="/browse" className="btn-dark" data-testid="dashboard-explore">Discover rooms <ArrowRight size={15} /></Link>
        </div>

        {rooms.length === 0 ? (
          <ol className="mt-8 grid md:grid-cols-3 gap-px bg-[var(--bhega-border)] border border-border" data-testid="dashboard-onboarding">
            {[
              { n: "01", t: "Pick what you plan to buy", s: "Search a car, bike, home or gadget you genuinely intend to purchase." },
              { n: "02", t: "Hit the ✊ Bhega button", s: "Choose your buying month — or 'as soon as possible'." },
              { n: "03", t: "Talk & negotiate together", s: "Share quotes in your room and approach sellers as a group." },
            ].map((s) => (
              <li key={s.n} className="bg-card p-6">
                <div className="text-sm font-extrabold text-brand num">{s.n}</div>
                <div className="font-extrabold text-lg mt-2">{s.t}</div>
                <p className="text-sm text-muted-foreground mt-1">{s.s}</p>
              </li>
            ))}
          </ol>
        ) : (
          <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-6">
            <div className="border border-border" data-testid="bhega-in-section">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
                <div className="font-extrabold">Your rooms <span className="text-muted-foreground font-semibold num">· {rooms.length}</span></div>
                {rooms.length > 5 && <button onClick={onShowAll} className="btn-ghost text-xs" data-testid="bhega-in-show-more">Show all {rooms.length}</button>}
              </div>
              <div className="divide-y divide-border">
                {rooms.slice(0, 5).map((it, i) => <BhegaInRow key={`${it.product_id}-${it.scope}`} item={it} index={i} />)}
              </div>
            </div>
            <div className="border border-border bg-card" data-testid="dashboard-activity">
              <div className="px-4 py-3 border-b border-border font-extrabold">What&apos;s new</div>
              {notifications.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">No new messages since your last visit. Start the conversation — post a quote or a question in one of your rooms.</div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications.slice(0, 4).map((n) => (
                    <Link key={`${n.product_id}-${n.scope}`} to={`/${n.product_slug}`} className="block px-4 py-3 hover:bg-raised transition-colors" data-testid={`dashboard-activity-${n.product_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm truncate">{n.product_name}</span>
                        <span className="badge-count">{n.unread_count}</span>
                      </div>
                      <p className="text-[13px] text-muted-foreground line-clamp-2 mt-0.5"><strong className="text-foreground">{n.latest_alias}:</strong> {n.latest_text}</p>
                      <div className="text-xs text-muted-foreground mt-1">{timeAgo(n.latest_at)}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, title, sub, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <div className="eyebrow mb-2">{eyebrow}</div>
        <h2 className="display-2">{title}</h2>
        {sub && <p className="text-muted-foreground mt-2 text-base">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function Categories({ categories }) {
  return (
    <section className="container-x py-16 sm:py-20">
      <SectionHead eyebrow="Categories" title="What are you buying?" sub="Every category is full of rooms where buyers are already gathering." />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--bhega-border)] border border-border stagger">
        {categories.map((cat, i) => (
          <Link key={cat.slug} to={`/${cat.slug}`} data-testid={`category-tile-${cat.slug}`} className="group bg-card hover:bg-[var(--bhega-primary-surface)] p-6 min-h-[200px] flex flex-col transition-colors">
            <div className="flex items-center justify-between text-xs font-extrabold num">
              <span>{String(i + 1).padStart(2, "0")}</span>
              <span className="text-muted-foreground">{cat.count} listings</span>
            </div>
            <div className="mt-auto">
              <div className="text-3xl font-extrabold tracking-[-0.035em]">{cat.label}</div>
              <div className="text-[13px] text-muted-foreground mt-1">{CAT_SUBTITLES[cat.slug] || "Explore rooms"}</div>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand">Explore <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="border-t border-border bg-card">
      <div className="container-x py-16 sm:py-20 grid lg:grid-cols-[1fr_1.5fr] gap-12">
        <div className="lg:sticky lg:top-28 self-start">
          <div className="eyebrow mb-2">How it works</div>
          <h2 className="display-2">Eight steps.<br /><span className="mark-yellow">One smarter</span> way to buy.</h2>
          <p className="mt-5 text-muted-foreground max-w-md">From signup to the showroom — BHEGA brings buyers together so no one negotiates alone.</p>
          <Link to="/how-it-works" className="btn-outline mt-6" data-testid="how-section-cta">See the full walkthrough <ArrowRight size={14} /></Link>
        </div>
        <ol className="grid sm:grid-cols-2 gap-px bg-[var(--bhega-border)] border border-border" data-testid="home-how-steps">
          {STEPS.map((row, idx) => (
            <li key={row.t} className="bg-card p-5 group" data-testid={`how-step-${idx}`}>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 bg-yellow-soft text-[var(--bhega-yellow-text)] flex items-center justify-center shrink-0 group-hover:bg-[#FFBE06] group-hover:text-[#111111] transition-colors">{row.i}</span>
                <span className="font-extrabold leading-tight"><span className="text-brand mr-1.5 num">{String(idx + 1).padStart(2, "0")}</span>{row.t}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{row.s}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default function HomePage({ onOpenAuth }) {
  const { user, city } = useAuth();
  const { rooms = [], notifications = [] } = useActivity() || {};
  const [trending, setTrending] = useState(null);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const isMember = user && user.role !== "admin";

  useEffect(() => {
    const cityParam = city ? { city } : {};
    api.get("/products", { params: { limit: 12, sort: "trending", ...cityParam } }).then((r) => setTrending(r.data.products || []));
    api.get("/stats/summary").then((r) => setStats(r.data));
    api.get("/categories").then((r) => setCategories(r.data.categories || []));
    api.get("/products", { params: { q: "Windsor", limit: 1, ...cityParam } }).then((r) => {
      if (r.data.products?.length) setFeatured(r.data.products[0]);
    });
  }, [city]);

  useEffect(() => {
    const onBhega = async (ev) => {
      const pid = ev?.detail?.productId;
      if (!pid) return;
      try {
        const r = await api.get(`/products/${pid}`, { params: city ? { city } : {} });
        setTrending((cur) => (cur || []).map((p) => (p.id === pid ? { ...p, ...r.data } : p)));
        setFeatured((cur) => (cur && cur.id === pid ? { ...cur, ...r.data } : cur));
      } catch (_) { /* noop */ }
    };
    window.addEventListener("bhega:changed", onBhega);
    return () => window.removeEventListener("bhega:changed", onBhega);
  }, [city]);

  const trendingBlock = (
    <section className="container-x py-16 sm:py-20 border-t border-border">
      <SectionHead
        eyebrow={city ? `Trending in ${city.split(",")[0]}` : "Trending"}
        title="Where buyers are gathering."
        sub="Rooms with the most united buyers and the liveliest conversations."
        action={<Link to="/browse" className="btn-outline" data-testid="trending-see-all">See all rooms <ArrowRight size={14} /></Link>}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger" data-testid="trending-grid">
        {trending === null
          ? [0, 1, 2, 3].map((i) => <ProductCardSkeleton key={i} />)
          : trending.slice(0, 8).map((p) => <ProductCardText key={p.id} product={p} />)}
      </div>
    </section>
  );

  return (
    <div>
      {isMember && <Dashboard user={user} rooms={rooms} notifications={notifications} onShowAll={() => setShowAll(true)} />}
      {isMember && rooms.length > 0 ? (
        <>
          {trendingBlock}
          <Categories categories={categories} />
        </>
      ) : (
        <>
          <Hero user={user} onOpenAuth={onOpenAuth} stats={stats} featured={featured} />
          <Categories categories={categories} />
          {trendingBlock}
        </>
      )}
      <HowItWorks />
      {showAll && <BhegaInModal items={rooms} onClose={() => setShowAll(false)} />}
    </div>
  );
}
